import {
  Autocomplete,
  AutocompleteRenderInputParams,
  AutocompleteRenderValueGetItemProps,
  Chip,
  FormControl,
  FormHelperText,
  Stack,
  SxProps,
  TextField,
  Theme,
} from '@mui/material'
import _ from 'lodash'
import { ChangeEvent, useState } from 'react'
import { Controller, FieldErrors, FieldPath, FieldValues } from 'react-hook-form'

import Tooltip from 'components/Tooltip'
import { inputWidth } from 'providers/material-ui/theme/constants'
import { TagsFieldProps } from './types'

export const TagsField = <T extends FieldValues>({
  control,
  disabled,
  disabledFieldTooltip,
  name,
  options,
  tooltip: tooltipProps,
  ...textFieldProps
}: TagsFieldProps<T>): React.JSX.Element => {
  const [inputValue, setInputValue] = useState<string>('')

  const calculatedWidth: number | string = inputWidth[textFieldProps.width ?? 'md']

  const containerSxProps: SxProps<Theme> = {
    minWidth: calculatedWidth,
  }

  const inputSxProps: SxProps<Theme> = {
    minWidth: calculatedWidth,
    width: calculatedWidth,
  }

  const chipSxProps = (isValid: boolean): SxProps<Theme> =>
    isValid
      ? {
          backgroundColor: ({ palette: { grey } }) => grey[200],
          color: ({ palette: { black } }) => black.main,
        }
      : {
          backgroundColor: ({ palette: { error } }) => error.main,
          color: ({ palette: { white } }) => white.main,
        }

  return (
    <Controller
      control={control}
      name={name as FieldPath<T>}
      render={({ field: { onChange, value }, fieldState: { error } }) => (
        <FormControl
          error={Boolean(error)}
          sx={containerSxProps}
        >
          <Stack
            direction='row'
            sx={containerSxProps}
          >
            <Autocomplete
              disableClearable
              disabled={disabled}
              freeSolo
              id={name}
              inputValue={inputValue}
              multiple
              onChange={(_, value: string[]): void => {
                onChange(value)
                setInputValue('')
              }}
              options={options || []}
              renderInput={(props: AutocompleteRenderInputParams): React.JSX.Element => {
                const Field = (
                  <TextField
                    {...props}
                    {...textFieldProps}
                    error={Boolean(error)}
                    onBlur={() => {
                      if (
                        inputValue === '' ||
                        value.some((tag: string): boolean => tag === inputValue)
                      ) {
                        return
                      }

                      onChange([...value, inputValue])
                      setInputValue('')
                    }}
                    onChange={(event: ChangeEvent<HTMLInputElement>): void => {
                      setInputValue(event.target.value)
                    }}
                    slotProps={{
                      ...props.slotProps,
                      inputLabel: {
                        ...props.slotProps.inputLabel,
                        shrink: true,
                      },
                    }}
                    sx={inputSxProps}
                  />
                )

                return disabledFieldTooltip && disabled ? (
                  <Tooltip
                    {...disabledFieldTooltip}
                    placement='top'
                  >
                    {Field}
                  </Tooltip>
                ) : (
                  Field
                )
              }}
              renderValue={(
                value: string[],
                getTagProps: AutocompleteRenderValueGetItemProps<true>,
              ) =>
                value.map((option: string, index: number): React.JSX.Element => {
                  let isValid: boolean = true
                  if (_.isArray(error) && error[index]) {
                    isValid = false
                  } else if (_.isPlainObject(error)) {
                    isValid = _.isEmpty((error as FieldErrors<T>)[index])
                  }

                  return (
                    <Chip
                      {...getTagProps({ index })}
                      key={option}
                      label={option}
                      sx={chipSxProps(isValid)}
                    />
                  )
                })
              }
              sx={containerSxProps}
              value={value}
            />

            {tooltipProps && <Tooltip {...tooltipProps} />}
          </Stack>

          {error && <FormHelperText>{error.message}</FormHelperText>}
        </FormControl>
      )}
    />
  )
}
